import type { Optional } from '@festina-life/core'
import dayjs from 'dayjs'

import { useScenarioContext } from '../context/scenarioContext'
import { useHook } from '../lenteHooks'
import { useGetEvents } from './useFilterCommands'
import { useTime } from './useTime'

type Options = {
  commitmentDate?: Optional<string>
  enabled: boolean
}

export const useFetchCommitmentVersionByDate = ({
  commitmentDate,
  enabled,
}: Options) => {
  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { data: time } = useTime()

  const isFutureDate = dayjs(commitmentDate).isAfter(time)
  const isPastOrCurrentDate = !isFutureDate
  const { scenarioIdForRequestHeader } = useScenarioContext()

  const simulatedCommitmentVersion = useHook(
    'fetchers.life.useFetchCommitmentVersionSimulation',
  )(
    {
      ownerId,
      tenantId,
      requestBody: {
        _type: 'CommitmentSimulationData',
        simulateTo: commitmentDate,
        commands: [],
      },
      scenarioId: scenarioIdForRequestHeader,
    },
    {
      enabled: enabled && isFutureDate,
    },
  )

  const { data: commitmentCommands } = useHook(
    'fetchers.life.useFetchCommitmentCommands',
  )(
    {
      tenantId,
      ownerId,
      scenarioId: scenarioIdForRequestHeader,
    },
    {
      enabled: enabled && isPastOrCurrentDate,
    },
  )

  const allEvents = useGetEvents(commitmentCommands?.abstractCommandVersions)

  const lastEventBeforeDate = allEvents.find(
    (event) =>
      event.applyDate &&
      new Date(event.applyDate).getTime() <=
        new Date(commitmentDate ?? '').getTime(),
  )

  const { eventId } = lastEventBeforeDate ?? {}

  const commitmentVersion = useHook('fetchers.life.useFetchCommitmentVersion')(
    { eventId: eventId ?? '', ownerId, tenantId },
    { enabled: enabled && !!eventId },
  )

  return isFutureDate ? simulatedCommitmentVersion : commitmentVersion
}
